'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function HeroSection({ onOpenModal }: { onOpenModal: () => void }) {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'view_landing');
    }
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center pt-16 overflow-hidden bg-white">
      
      {/* Atmospheric background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-gradient-to-bl from-accent/8 via-transparent to-transparent blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-primary/6 via-transparent to-transparent blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-radial from-highlight/4 to-transparent blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* LEFT: Content */}
          <div className="space-y-8">
            {/* Trust badge */}
            <div className="inline-flex items-center gap-2 bg-surface border border-border rounded-full px-4 py-2 animate-fade-in-up">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-xs font-semibold text-gray-600">
                <span className="text-foreground font-bold">247 Genies</span> available right now
              </span>
            </div>

            {/* Headline */}
            <div className="space-y-2" style={{ animation: 'fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both' }}>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.0] tracking-tight text-foreground">
                Summon
                <br />
                <span className="gradient-text-primary">Marketing Help.</span>
                <br />
                Instantly.
              </h1>
            </div>

            {/* Subheadline */}
            <p
              className="text-lg sm:text-xl text-muted leading-relaxed max-w-lg"
              style={{ animation: 'fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both' }}>
              Describe what you need → get instantly paired with a live Genie → start working together → pay securely via escrow.
            </p>

            {/* Join Waitlist CTA */}
            <div style={{ animation: 'fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both' }}>
              <Link
                href="/waitlist"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-2xl font-bold text-base hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0">
                <span>Join the Waitlist</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>

            {/* Coming Soon Store Badges */}
            <div
              className="flex flex-col sm:flex-row gap-3"
              style={{ animation: 'fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.35s both' }}>
              
              {/* iOS - Coming Soon */}
              <div
                className="flex items-center justify-center gap-3 bg-foreground text-white px-6 py-4 rounded-2xl font-semibold text-sm opacity-60 cursor-not-allowed select-none">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
                <div className="text-left">
                  <div className="text-[10px] opacity-70 font-normal">Coming Soon to the</div>
                  <div className="text-base font-bold leading-tight">App Store</div>
                </div>
              </div>

              {/* Android - Coming Soon */}
              <div
                className="flex items-center justify-center gap-3 bg-gray-700 text-white px-6 py-4 rounded-2xl font-semibold text-sm opacity-60 cursor-not-allowed select-none">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1367 1.0989L4.841 5.4467a.4161.4161 0 00-.5677-.1521.4157.4157 0 00-.1521.5676l1.9973 3.4592C3.8592 10.3902 2.3 12.3 2 14.6h20c-.3-2.3-1.8592-4.2098-4.1185-5.2786" /></svg>
                <div className="text-left">
                  <div className="text-[10px] opacity-70 font-normal">Coming Soon on</div>
                  <div className="text-base font-bold leading-tight">Google Play</div>
                </div>
              </div>
            </div>

            {/* Microcopy */}
            <p
              className="text-xs text-muted flex items-center gap-1.5"
              style={{ animation: 'fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.4s both' }}>
              <Icon name="LockClosedIcon" size={12} className="text-muted" />
              Free to use. Start in seconds. No subscription required.
            </p>
          </div>

          {/* RIGHT: Phone + Floating Cards */}
          <div className="relative flex justify-center lg:justify-end" style={{ animation: 'fadeInScale 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both' }}>
            <div className="relative w-[280px] sm:w-[320px]">

              {/* Phone mockup */}
              <div className="relative w-full aspect-[9/19] z-10">
                <AppImage
                  src="/assets/images/summon_phone-1771557785074.png"
                  alt="Summons app live session screen showing expert connection and screen sharing"
                  fill
                  className="w-full h-full object-contain" />
              </div>

              {/* Floating Card 1 - Expert Available */}
              <div className="absolute -left-16 top-16 glass-card rounded-2xl px-3 py-2.5 flex items-center gap-2.5 animate-float z-20 shadow-card">
                <div className="w-8 h-8 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm">🧞</span>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-foreground">Genie Available</p>
                  <p className="text-[9px] text-muted">Matched in 8 sec</p>
                </div>
              </div>

              {/* Floating Card 2 - Instant Payout */}
              <div className="absolute -right-12 top-32 glass-card rounded-2xl px-3 py-2.5 flex items-center gap-2.5 animate-float-delayed z-20 shadow-card">
                <div className="w-8 h-8 rounded-xl bg-yellow-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm">⚡</span>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-foreground">Instant Payout</p>
                  <p className="text-[9px] text-muted">$240 released</p>
                </div>
              </div>

              {/* Floating Card 3 - Screen Share */}
              <div className="absolute -left-14 bottom-40 glass-card rounded-2xl px-3 py-2.5 flex items-center gap-2.5 animate-float-slow z-20 shadow-card">
                <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF3008" strokeWidth="2.5"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-foreground">Live Screen Share</p>
                  <p className="text-[9px] text-muted">No extra tools</p>
                </div>
              </div>

              {/* Floating Card 4 - Rating */}
              <div className="absolute -right-10 bottom-28 glass-card rounded-2xl px-3 py-2.5 flex items-center gap-2.5 animate-float z-20 shadow-card" style={{ animationDelay: '2s' }}>
                <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm">⭐</span>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-foreground">4.9 / 5.0</p>
                  <p className="text-[9px] text-muted">Avg Genie rating</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
